import Phaser from 'phaser';

const PARTICLE_COUNT = 80;
const FIREWORK_COUNT = 6;
const CONFETTI_COLORS = [
  0xff0000, 0x00ff00, 0xffff00, 0xff00ff, 0x00ffff,
  0xff8800, 0x8800ff, 0x00ff88, 0xff0088, 0xffffff,
];
const FIREWORK_BURST_COLORS = [
  0xffff00, 0xff4400, 0x00ffcc, 0xff00ff, 0x44aaff,
];

interface ConfettiPiece {
  readonly graphics: Phaser.GameObjects.Rectangle;
  velocityX: number;
  velocityY: number;
  rotationSpeed: number;
  gravity: number;
}

interface FireworkBurst {
  readonly particles: ReadonlyArray<{
    readonly graphics: Phaser.GameObjects.Arc;
    velocityX: number;
    velocityY: number;
    life: number;
  }>;
  age: number;
}

export class ConfettiEffect {
  private scene: Phaser.Scene;
  private confettiPieces: ConfettiPiece[] = [];
  private fireworkBursts: FireworkBurst[] = [];
  private isActive = false;
  private elapsed = 0;
  private nextFireworkTime = 0;
  private fireworksLaunched = 0;
  private areaWidth: number;
  private areaHeight: number;

  constructor(scene: Phaser.Scene, areaWidth: number, areaHeight: number) {
    this.scene = scene;
    this.areaWidth = areaWidth;
    this.areaHeight = areaHeight;
  }

  start(): void {
    this.isActive = true;
    this.elapsed = 0;
    this.fireworksLaunched = 0;
    this.nextFireworkTime = 100;
    this.spawnConfetti();
  }

  update(delta: number): void {
    if (!this.isActive) {
      return;
    }

    this.elapsed += delta;

    // Update confetti pieces
    for (const piece of this.confettiPieces) {
      piece.velocityY += piece.gravity * (delta / 1000);
      piece.graphics.x += piece.velocityX * (delta / 1000);
      piece.graphics.y += piece.velocityY * (delta / 1000);
      piece.graphics.rotation += piece.rotationSpeed * (delta / 1000);

      // Slow horizontal drift
      piece.velocityX *= 0.998;

      // Fade out as it falls past the screen
      if (piece.graphics.y > this.areaHeight - 20) {
        piece.graphics.alpha -= 0.02;
      }
    }

    // Launch firework bursts at intervals
    if (this.elapsed >= this.nextFireworkTime && this.fireworksLaunched < FIREWORK_COUNT) {
      this.launchFirework();
      this.fireworksLaunched++;
      this.nextFireworkTime = this.elapsed + 300 + Math.random() * 250;
    }

    // Update firework bursts
    for (const burst of this.fireworkBursts) {
      burst.age += delta;
      for (const particle of burst.particles) {
        particle.life -= delta;
        particle.graphics.x += particle.velocityX * (delta / 1000);
        particle.graphics.y += particle.velocityY * (delta / 1000);
        particle.velocityY += 40 * (delta / 1000); // gravity
        particle.velocityX *= 0.99;

        const lifeRatio = Math.max(0, particle.life / 800);
        particle.graphics.alpha = lifeRatio;
        particle.graphics.setScale(lifeRatio * 0.8 + 0.2);
      }
    }

    // Clean up dead firework particles
    this.cleanupFireworks();
  }

  destroy(): void {
    this.isActive = false;
    for (const piece of this.confettiPieces) {
      piece.graphics.destroy();
    }
    this.confettiPieces = [];
    for (const burst of this.fireworkBursts) {
      for (const particle of burst.particles) {
        particle.graphics.destroy();
      }
    }
    this.fireworkBursts = [];
  }

  private spawnConfetti(): void {
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const x = Math.random() * this.areaWidth;
      const y = -10 - Math.random() * 60;
      const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)] ?? 0xffffff;
      const width = 4 + Math.random() * 5;
      const height = 3 + Math.random() * 4;

      const rect = this.scene.add.rectangle(x, y, width, height, color);
      rect.setDepth(100);
      rect.setAlpha(0.9);

      this.confettiPieces.push({
        graphics: rect,
        velocityX: (Math.random() - 0.5) * 100,
        velocityY: 20 + Math.random() * 50,
        rotationSpeed: (Math.random() - 0.5) * 8,
        gravity: 15 + Math.random() * 25,
      });
    }
  }

  private launchFirework(): void {
    const burstX = 40 + Math.random() * (this.areaWidth - 80);
    const burstY = 60 + Math.random() * (this.areaHeight * 0.4);
    const color = FIREWORK_BURST_COLORS[
      Math.floor(Math.random() * FIREWORK_BURST_COLORS.length)
    ] ?? 0xffffff;

    const particleCount = 16 + Math.floor(Math.random() * 10);
    const particles: Array<{
      readonly graphics: Phaser.GameObjects.Arc;
      velocityX: number;
      velocityY: number;
      life: number;
    }> = [];

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.3;
      const speed = 60 + Math.random() * 80;
      const circle = this.scene.add.circle(burstX, burstY, 3, color);
      circle.setDepth(101);
      circle.setAlpha(1);

      particles.push({
        graphics: circle,
        velocityX: Math.cos(angle) * speed,
        velocityY: Math.sin(angle) * speed,
        life: 600 + Math.random() * 400,
      });
    }

    this.fireworkBursts.push({
      particles,
      age: 0,
    });
  }

  private cleanupFireworks(): void {
    const activeBursts: FireworkBurst[] = [];
    for (const burst of this.fireworkBursts) {
      const allDead = burst.particles.every((p) => p.life <= 0);
      if (allDead) {
        for (const particle of burst.particles) {
          particle.graphics.destroy();
        }
      } else {
        activeBursts.push(burst);
      }
    }
    this.fireworkBursts = activeBursts;
  }
}
